import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { type Observable, forkJoin, map, switchMap } from 'rxjs';
import { RuntimeConfigService } from '../config/runtime-config';
import {
  type MeResponse,
  type MyPermissionsResponse,
  type ScopeSummary,
  authChangePassword,
  authForgotPassword,
  authMe$Json,
  authRefresh,
  authResetPassword,
  authSignIn$Json,
  authSignOutCurrent,
  authzMyPermissions$Json,
} from '../services/api';
import { type DataScope, type SessionSnapshot } from './session.store';

export interface SignInCredentials {
  readonly login: string;
  readonly password: string;
}

/**
 * The seam between the session code and the server. `AuthService` talks only to this, so the
 * real HTTP calls and the in-memory stand-in are interchangeable. Which one runs is decided
 * once, by `provideAuthApi`, from the `mockApi` setting in `assets/config.json` (off by default).
 *
 * Every method fails with an `ApiError` (the error interceptor maps HTTP failures to one).
 * Sign-in and the cookies it sets are the server's business: nothing here sees a token.
 */
export abstract class AuthApi {
  /** Signs in and returns the full session. Fails with `auth.invalid_credentials` on a bad login. */
  abstract signIn(credentials: SignInCredentials): Observable<SessionSnapshot>;
  /** The current session from the cookie. Fails with a 401 when there is none. */
  abstract me(): Observable<SessionSnapshot>;
  /** Rotates the session cookies. Completes when the server accepted the refresh. */
  abstract refresh(): Observable<void>;
  /** Ends the session on the server and clears its cookies. */
  abstract signOut(): Observable<void>;
  /** Changes the signed-in user's password. Fails with `auth.password_weak` or a 422. */
  abstract changePassword(currentPassword: string, newPassword: string): Observable<void>;
  /** Asks for a reset link. The server answers the same whether or not the login exists. */
  abstract forgotPassword(login: string): Observable<void>;
  /** Sets a new password from a reset link token. Fails with `auth.reset_token_invalid`. */
  abstract resetPassword(token: string, newPassword: string): Observable<void>;
}

/** Widest first. The API reports one entry per scope kind a user holds. */
const SCOPE_ORDER: readonly DataScope[] = ['all', 'department', 'reports', 'own'];

/**
 * The widest scope in `scopes`, or undefined when there are none or none is recognised.
 * The API still filters every list by the exact scope; this only drives what the UI offers.
 */
export function widestScope(scopes: readonly ScopeSummary[]): DataScope | undefined {
  const held = new Set(scopes.map((s) => s.kind.toLowerCase()));
  return SCOPE_ORDER.find((kind) => held.has(kind));
}

/** Real implementation: cookie session over HTTP. The interceptors add credentials and CSRF. */
export class HttpAuthApi extends AuthApi {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  private get root(): string {
    return this.config.config().apiBaseUrl;
  }

  signIn(credentials: SignInCredentials): Observable<SessionSnapshot> {
    // The sign-in response carries no permissions, so the session is read back from /me.
    return authSignIn$Json(this.http, this.root, { body: credentials }).pipe(
      switchMap(() => this.me()),
    );
  }

  me(): Observable<SessionSnapshot> {
    return forkJoin([
      authMe$Json(this.http, this.root).pipe(map((r) => r.body)),
      authzMyPermissions$Json(this.http, this.root).pipe(map((r) => r.body)),
    ]).pipe(map(([me, mine]) => toSnapshot(me, mine)));
  }

  refresh(): Observable<void> {
    return authRefresh(this.http, this.root).pipe(map(() => undefined));
  }

  signOut(): Observable<void> {
    return authSignOutCurrent(this.http, this.root).pipe(map(() => undefined));
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return authChangePassword(this.http, this.root, {
      body: { currentPassword, newPassword },
    }).pipe(map(() => undefined));
  }

  forgotPassword(login: string): Observable<void> {
    return authForgotPassword(this.http, this.root, { body: { login } }).pipe(map(() => undefined));
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return authResetPassword(this.http, this.root, { body: { token, newPassword } }).pipe(
      map(() => undefined),
    );
  }
}

function toSnapshot(me: MeResponse, mine: MyPermissionsResponse): SessionSnapshot {
  return {
    user: {
      id: me.user.id,
      displayName: me.user.displayName,
      mustChangePassword: me.user.mustChangePassword,
    },
    // The wire object also carries the company id. It is dropped here on purpose.
    company: { name: me.tenant.name, timeZone: me.tenant.timeZone },
    // `/me/permissions` is the same set plus scopes; `/me` is kept as the source of the codes.
    permissions: me.permissions,
    dataScope: widestScope(mine.scopes),
    // The API has no roles in /me and no feature-flag endpoint yet (flags arrive with P015).
    roles: [],
    flags: [],
  };
}
